/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { IDisposableDependency } from "@adapptio/lib-core";
import { dataEqual } from "@adapptio/lib-blueprint";
import { BrowserLogger } from "../common/BrowserLogger";
import { detectOperatingSystem, OS_NAME } from "../common/Env";
import { EditorConfigService } from "../config/EditorConfigService";
import { IEditor } from "../IEditor";
import { IKeyBinding, IKeyBindingEntry, IKeyboardEvent, IKeyCombinationListItem } from "./IKeyBinding";
import {
	hashKeyCombination,
	keyCombinationToString,
	resolveKeyCombinationFromEvent
} from "./KeyBindingHelpers";

interface IKeyBindingRegistry {
	[K: string]: IKeyBindingEntry;
}

/**
 * Map of os -> keyHash -> keyBinding
 */
interface IKeyBindingHashIndex {
	win: { [K: string]: IKeyBindingEntry[] };
	linux: { [K: string]: IKeyBindingEntry[] };
	mac: { [K: string]: IKeyBindingEntry[] };
	all: { [K: string]: IKeyBindingEntry[] };
}

/**
 * Map of commandId -> entry
 */
interface ICommandIndex {
	[K: string]: IKeyBindingEntry[];
}

interface IBindingConfigItem {
	id: string;
	keys: IKeyCombinationListItem[];
}

/**
 * Service options
 */
export interface IKeyBindingServiceOpts {}

/**
 * Service to manage key bindings
 */
export class KeyBindingService implements IDisposableDependency {
	private static CONFIG_KEY__BINDINGS = "keyBindingsService.bindings";

	/** Logger facility */
	private logger: BrowserLogger;

	/** Config service */
	private editorConfigSvc: EditorConfigService;

	/** Registered key bindings */
	private registry: IKeyBindingRegistry = {};

	/** Key index for fast lookup */
	private keyIndex: IKeyBindingHashIndex = {
		win: {},
		linux: {},
		mac: {},
		all: {}
	};

	/** Command index */
	private commandIndex: ICommandIndex = {};

	/** Current operating system */
	private currentOs: OS_NAME;

	/**
	 * Service Constructor
	 *
	 * @param dc Dependency Container
	 * @param opts Configuration options
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public constructor(private dc: IEditor, opts: IKeyBindingServiceOpts) {
		this.logger = dc.get("logger").facility("KeyBindingService");
		this.editorConfigSvc = dc.get("editorConfigService");

		this.currentOs = detectOperatingSystem();

		this.editorConfigSvc.register<IBindingConfigItem[]>(
			KeyBindingService.CONFIG_KEY__BINDINGS,
			keyBindingsConfigSchema,
			[]
		);
	}

	/**
	 * Register key binding
	 *
	 * @param keyBinding Key binding
	 */
	public register(keyBinding: IKeyBinding): void {
		if (this.registry[keyBinding.id]) {
			throw new Error(`Key binding ID '${keyBinding.id}' is already registered.`);
		}

		const entry = {
			...keyBinding,
			keys: keyBinding.keys.slice(),
			originalKeys: keyBinding.keys.slice(),
			isUserOverriden: false
		};

		this.registry[keyBinding.id] = entry;

		if (!this.commandIndex[keyBinding.commandId]) {
			this.commandIndex[keyBinding.commandId] = [];
		}

		this.commandIndex[keyBinding.commandId].push(entry);
	}

	/**
	 * Returns if a key binding is registered
	 *
	 * @param bindingId Key binding ID
	 */
	public isRegistered(bindingId: string): boolean {
		return this.registry[bindingId] ? true : false;
	}

	/**
	 * Returns key binding entry
	 *
	 * @param bindingId Key binding ID
	 */
	public getBindingEntry(bindingId: string): IKeyBindingEntry {
		return this.registry[bindingId] || null;
	}

	/**
	 * Returns entire key binding registry
	 */
	public getRegistry(): IKeyBindingRegistry {
		return this.registry;
	}

	/**
	 * Returns a list of registered key bindings
	 */
	public getAllBindingsList(): IKeyBindingEntry[] {
		return Object.keys(this.registry).map((k) => this.registry[k]);
	}

	/**
	 * Returns a list of key combinations associated with given binding ID
	 *
	 * @param bindingId Key binding ID
	 */
	public getKeyList(bindingId: string): IKeyCombinationListItem[] {
		const stack = this.registry[bindingId];
		return stack ? stack.keys : [];
	}

	/**
	 * Replaces all key associated with the binding
	 *
	 * @param bindingId Key binding ID
	 * @param keys Keys
	 */
	public replaceKeyList(bindingId: string, keys: IKeyCombinationListItem[]): void {
		const entry = this.registry[bindingId];

		if (!entry) {
			throw new Error(`Key binding ID '${bindingId}' is not registered.`);
		}

		entry.keys = keys;
		entry.isUserOverriden = true;

		this.buildIndex();
		this.storeConfig();
	}

	/**
	 * Adds key combination to a binding
	 *
	 * @param bindingId Key binding ID
	 * @param key Key
	 */
	public addKey(bindingId: string, key: IKeyCombinationListItem): void {
		const entry = this.registry[bindingId];

		if (!entry) {
			throw new Error(`Key binding ID '${bindingId}' is not registered.`);
		}

		entry.keys.push(key);
		entry.isUserOverriden = true;

		this.buildIndex();
		this.storeConfig();
	}

	/**
	 * Updates key combination of a binding
	 *
	 * @param bindingId Key binding ID
	 * @param index Key index
	 */
	public updateKey(bindingId: string, index: number, key: IKeyCombinationListItem): void {
		const entry = this.registry[bindingId];

		if (!entry) {
			throw new Error(`Key binding ID '${bindingId}' is not registered.`);
		}

		if (!entry.keys[index]) {
			throw new Error(`Key binding ID '${bindingId}' has no key combination at index '${index}'.`);
		}

		entry.keys[index] = key;
		entry.isUserOverriden = true;

		this.buildIndex();
		this.storeConfig();
	}

	/**
	 * Removes key combination from a binding
	 *
	 * @param bindingId Key binding ID
	 * @param index Key index
	 */
	public removeKey(bindingId: string, index: number): void {
		const entry = this.registry[bindingId];

		if (!entry) {
			throw new Error(`Key binding ID '${bindingId}' is not registered.`);
		}

		if (!entry.keys[index]) {
			throw new Error(`Key binding ID '${bindingId}' has no key combination at index '${index}'.`);
		}

		entry.keys.splice(index, 1);
		entry.isUserOverriden = true;

		this.buildIndex();
		this.storeConfig();
	}

	/**
	 * Removes all key combination from a binding
	 *
	 * @param bindingId Key binding ID
	 */
	public clearKeys(bindingId: string): void {
		const entry = this.registry[bindingId];

		if (!entry) {
			throw new Error(`Key binding ID '${bindingId}' is not registered.`);
		}

		entry.keys = [];
		entry.isUserOverriden = true;

		this.buildIndex();
		this.storeConfig();
	}

	/**
	 * Resets binding key combinatons to default ones
	 *
	 * @param bindingId Key binding ID
	 */
	public resetKeysToDefault(bindingId: string): void {
		const entry = this.registry[bindingId];

		if (!entry) {
			throw new Error(`Key binding ID '${bindingId}' is not registered.`);
		}

		entry.keys = entry.originalKeys.slice();
		entry.isUserOverriden = true;

		this.buildIndex();
		this.storeConfig();
	}

	/**
	 * Returns key binding entries for a given command and its arguments
	 *
	 * @param commandId Command ID
	 * @param args Optional command arguments
	 * @returns
	 */
	public getBindingsForCommand(commandId: string, args?: unknown[]): IKeyBindingEntry[] {
		const stack = this.commandIndex[commandId];
		return stack
			? stack.filter((item) => {
					if (args !== undefined) {
						return dataEqual(item.commandArgs, args);
					} else {
						return true;
					}
			  })
			: [];
	}

	/**
	 * Returns key for a command
	 *
	 * @param commandId Command ID
	 * @param args Command arguments
	 */
	public getKeysForCommand(commandId: string, args?: unknown[]): IKeyCombinationListItem[] {
		const entries = this.getBindingsForCommand(commandId, args).sort(sortEntriesByPriority);
		const res: IKeyCombinationListItem[] = [];

		for (let i = 0; i < entries.length; i++) {
			for (let j = 0; j < entries[i].keys.length; j++) {
				res.push(entries[i].keys[j]);
			}
		}

		return res;
	}

	/**
	 * Returns key for a command by OS
	 *
	 * @param os Operating system name
	 * @param commandId Command ID
	 * @param args Command arguments
	 */
	public getKeysForCommandByOs(
		os: OS_NAME,
		commandId: string,
		args?: unknown[]
	): IKeyCombinationListItem[] {
		const entries = this.getBindingsForCommand(commandId, args).sort(sortEntriesByPriority);

		const res: IKeyCombinationListItem[] = [];

		for (let i = 0; i < entries.length; i++) {
			for (let j = 0; j < entries[i].keys.length; j++) {
				const item = entries[i].keys[j];

				if (
					(item.isWin && os === OS_NAME.WINDOWS) ||
					(item.isLinux && os === OS_NAME.LINUX) ||
					(item.isMac && os === OS_NAME.MACOS) ||
					(!item.isWin && !item.isLinux && item.isMac)
				) {
					res.push(entries[i].keys[j]);
				}
			}
		}

		return res;
	}

	/**
	 * Returns key combinations for a command and current OS as a string
	 *
	 * @param commandId Command ID
	 * @param args Command arguments
	 * @param keyDelimiter String to put between keys in a combination
	 * @param combinationDelimiter String to put between key combinations
	 */
	public getKeysForCommandAsString(
		commandId: string,
		args?: unknown[],
		keyDelimiter = "+",
		combinationDelimiter = " / "
	): string {
		const keys = this.getKeysForCommandByOs(this.currentOs, commandId, args);

		return keys
			.map((item) => keyCombinationToString(item.key).join(keyDelimiter))
			.join(combinationDelimiter);
	}

	/**
	 * Returns key bindings matching keyboard event, sorted by priority
	 *
	 * @param event Keyboard event
	 * @param os Operating system
	 * @returns
	 */
	public getBindingsForEvent(event: IKeyboardEvent, os?: OS_NAME): IKeyBindingEntry[] {
		const keyCombination = resolveKeyCombinationFromEvent(event);
		const hash = hashKeyCombination(keyCombination);

		let entries: IKeyBindingEntry[];
		switch (os) {
			case OS_NAME.WINDOWS: {
				entries = this.keyIndex.win[hash] || [];
				break;
			}
			case OS_NAME.LINUX: {
				entries = this.keyIndex.linux[hash] || [];
				break;
			}
			case OS_NAME.MACOS: {
				entries = this.keyIndex.mac[hash] || [];
				break;
			}
			default: {
				entries = this.keyIndex.all[hash] || [];
			}
		}

		return entries.sort(sortEntriesByPriority);
	}

	/**
	 * Returns if there is any key binding for a specified event
	 *
	 * @param event Keyboard Event
	 */
	public hasBindingForKeyEvent(event: IKeyboardEvent): boolean {
		const bindings = this.getBindingsForEvent(event, this.currentOs).filter((item) =>
			item.when ? item.when() : true
		);

		return bindings.length > 0;
	}

	/**
	 * Dispatches keyboard event
	 * Tries to find corresponding key bindings and executes associated command
	 *
	 * @param event Keyborad event
	 */
	// Promise is returned for a better testing
	public async dispatchKeyEvent(event: IKeyboardEvent): Promise<void> {
		const bindings = this.getBindingsForEvent(event, this.currentOs).filter((item) =>
			item.when ? item.when() : true
		);

		if (bindings.length === 0) {
			return;
		}

		const entry = bindings[0];

		this.logger.debug(`Dispatching command '${entry.commandId}' by key binding '${entry.id}'.`);

		try {
			await this.dc.get("commandsService").execute(entry.commandId, ...(entry.commandArgs || []));

			this.logger.debug(`Command ID '${entry.commandId}' sucessfully executed.`);
			// @todo show optional notification
		} catch (err) {
			this.logger.error(`Failed to execute command '${entry.commandId}': ${err}`);
			// @todo Show notification
		}
	}

	/**
	 * Initializes services
	 */
	public async init(): Promise<void> {
		this.restoreFromConfig();
		this.buildIndex();
	}

	/**
	 * Disposes service
	 */
	public async dispose(): Promise<void> {
		// Do nothing here
	}

	private buildIndex() {
		this.keyIndex = {
			win: {},
			linux: {},
			mac: {},
			all: {}
		};

		for (const k in this.registry) {
			const entry = this.registry[k];

			for (let i = 0; i < entry.keys.length; i++) {
				const item = entry.keys[i];

				const hash = String(hashKeyCombination(item.key));

				if (item.isWin) {
					if (!this.keyIndex.win[hash]) {
						this.keyIndex.win[hash] = [];
					}

					this.keyIndex.win[hash].push(entry);
				}

				if (item.isLinux) {
					if (!this.keyIndex.linux[hash]) {
						this.keyIndex.linux[hash] = [];
					}

					this.keyIndex.linux[hash].push(entry);
				}

				if (item.isMac) {
					if (!this.keyIndex.mac[hash]) {
						this.keyIndex.mac[hash] = [];
					}

					this.keyIndex.mac[hash].push(entry);
				}

				if (!this.keyIndex.all[hash]) {
					this.keyIndex.all[hash] = [];
				}

				this.keyIndex.all[hash].push(entry);
			}
		}
	}

	private restoreFromConfig(): void {
		this.logger.debug("Restoring user key bindings config.");

		const config = this.editorConfigSvc.get<IBindingConfigItem[]>(KeyBindingService.CONFIG_KEY__BINDINGS);

		for (let i = 0; i < config.length; i++) {
			const item = config[i];
			const entry = this.registry[item.id];

			if (!entry) {
				this.logger.warn(
					`Cannot resotre key binding config item: Binding ID '${item.id}' is not registered.`
				);
				continue;
			}

			entry.keys = item.keys.slice();
			entry.isUserOverriden = true;
		}
	}

	private storeConfig(): void {
		const config: IBindingConfigItem[] = [];

		for (const k in this.registry) {
			const entry = this.registry[k];

			if (entry.isUserOverriden) {
				config.push({
					id: entry.id,
					keys: entry.keys.slice()
				});
			}
		}

		this.editorConfigSvc.set(KeyBindingService.CONFIG_KEY__BINDINGS, config);
	}
}

function sortEntriesByPriority(a: IKeyBindingEntry, b: IKeyBindingEntry) {
	return a.priority > b.priority ? -1 : a.priority < b.priority ? 1 : 0;
}

const keyBindingsConfigSchema = {
	type: "array",
	items: {
		type: "object",
		required: [ "id", "keys" ],
		properties: {
			id: {
				type: "string"
			},
			keys: {
				type: "array",
				items: {
					type: "object",
					required: [ "key" ],
					properties: {
						key: {
							type: "object",
							properties: {
								ctrlKey: {
									type: "boolean"
								},
								shiftKey: {
									type: "boolean"
								},
								altKey: {
									type: "boolean"
								},
								metaKey: {
									type: "boolean"
								},
								keyCode: {
									type: "integer"
								}
							}
						},
						isWin: {
							type: "boolean"
						},
						isLinux: {
							type: "boolean"
						},
						isMac: {
							type: "boolean"
						}
					}
				}
			}
		}
	}
};
